import { computed, signal } from '@angular/core';
import { AntragsFlag, AntragsType, FirstBelegMandatoryConfiguration, Guid } from '@ebeihilfe/core';
import { AntragFinishDto } from '@ebeihilfe/einreichung-shared/dto/antrag-finish.dto';
import { AntragMetaData, LegacyAntragsMetadata } from '@ebeihilfe/einreichung-shared/model';

import { Beleg } from './Beleg';
import { nextMonotonicNumber } from './monotonic-number';
import { Seite } from './Seite';

export class Antrag {
  private readonly _antragsMetadata: LegacyAntragsMetadata[] | AntragMetaData[];
  private readonly _belege = signal<Beleg[]>([]);

  public doNotFinishMe = false;
  public readonly firstBelegMandatoryConfig: FirstBelegMandatoryConfiguration;
  public hasAcceptedDataProtection!: boolean;

  public hasSeiten = computed(() => this._belege().some((beleg) => beleg.seiten.length > 0));

  /** id of antrag, can only be set by backend */
  private _antragsId!: Guid;
  private _antragsType: AntragsType;
  private _isCompleted = false;
  private _belegCounter = 0;
  /** time of creation, can only be set by backend */
  private _timestamp!: string;
  private _containerId!: string;
  private _isPristine = true;
  private _surveyData?: Record<string, unknown>;

  constructor(firstBelegMandatoryConfig?: FirstBelegMandatoryConfiguration) {
    this.firstBelegMandatoryConfig = firstBelegMandatoryConfig ?? {
      isActive: false,
      mandatoryPages: 0,
    };
    this._antragsMetadata = [];
    this._antragsType = AntragsType.UNDEFINED;
  }

  public get hasPendingData(): boolean {
    return !this.isComplete && this.hasSeiten();
  }

  public get antragsId(): Guid {
    return this._antragsId;
  }

  public get antragsType(): AntragsType {
    return this._antragsType;
  }

  public get belege(): Beleg[] {
    return this._belege();
  }

  public set belege(value: Beleg[]) {
    this._belege.set(value);
  }

  public get isComplete(): boolean {
    return this._isCompleted;
  }

  public get timestamp(): string {
    return this._timestamp;
  }

  public set timestamp(value: string) {
    this._timestamp = value;
  }

  public get isLangantrag(): boolean {
    return this.antragsType === AntragsType.LANGANTRAG_LEGACY || this.antragsType === AntragsType.LANGANTRAG;
  }

  public get isFeatureDedicatedBelegEnabledOnAntrag(): boolean {
    return this.firstBelegMandatoryConfig.isActive;
  }

  public get containerId(): string {
    return this._containerId;
  }

  public set containerId(value: string) {
    this._containerId = value;
  }

  public get isPristine(): boolean {
    return this._isPristine;
  }

  public set isPristine(value: boolean) {
    this._isPristine = value;
  }

  /** The raw SurveyJS model data of a saved draft (decrypted from the backend's surveyDaten). */
  public get surveyData(): Record<string, unknown> | undefined {
    return this._surveyData;
  }

  public setSurveyData(surveyData: Record<string, unknown> | undefined): void {
    this._surveyData = surveyData;
  }

  public getAntragFinishData(encryptedMetadata: string): AntragFinishDto {
    return new AntragFinishDto(
      this._antragsId.toString(),
      this._belege().filter((b) => b.seiten.length > 0).length,
      this.getSeitenLength(),
      this._antragsType,
      new Date(this._timestamp),
      encryptedMetadata,
      this.getFlags(),
    );
  }

  public async getSizeInBytes(): Promise<number> {
    if (!this.hasBelege() && !this.hasSeiten()) {
      return 0;
    }

    return this._belege().reduce(
      async (size, beleg) => (await size) + (await beleg.getBelegSize()),
      Promise.resolve(0),
    );
  }

  public setComplete(): void {
    this._isCompleted = true;
  }

  public setAntragsType(antragsType: AntragsType): void {
    this._antragsType = antragsType;
  }

  public setAntragsData(antragsData: LegacyAntragsMetadata[] | AntragMetaData[]): void {
    if (antragsData.length === 0) {
      return;
    }
    this._antragsMetadata.length = 0;
    // Type assertion to handle the type mismatch
    this._antragsMetadata.push(...(antragsData as (LegacyAntragsMetadata & AntragMetaData)[]));
  }

  public addBeleg(belegNumber?: number): Beleg {
    const newBeleg = new Beleg(this.nextBelegNumber(belegNumber));
    this._belege.set([...this._belege(), newBeleg]);
    return newBeleg;
  }

  public hasAntragsMetadata(): boolean {
    return this._antragsMetadata.length > 0;
  }

  public getSeitenLength(): number {
    return this.getAllSeiten().length;
  }

  public canAddBeleg(): boolean {
    const belegLength = this.belege.length;

    if (belegLength === 0) {
      return true;
    }

    const lastBeleg = this.belege[belegLength - 1];
    return lastBeleg?.seiten.length > 0;
  }

  public hasBelege(): boolean {
    return this._belege()?.length > 0;
  }

  public getAllSeiten(): Seite[] {
    return this._belege()?.reduce((a: Seite[], b: Beleg) => a.concat(b.seiten), []) ?? [];
  }

  public setDataAfterCreationOrReset(antragsId: Guid, timestamp: string): void {
    this._antragsId = antragsId;
    this._timestamp = timestamp;
  }

  public getAntragMetaData(): AntragMetaData[] {
    if (this._antragsMetadata.length > 0 && !('formResults' in this._antragsMetadata[0])) {
      throw new Error('Expected AntragMetaData, not LegacyAntragsMetadata');
    }
    return this._antragsMetadata as AntragMetaData[];
  }

  public getLegacyAntragMetadata(): LegacyAntragsMetadata[] {
    if (this._antragsMetadata.length > 0 && 'formResults' in this._antragsMetadata[0]) {
      throw new Error('Expected LegacyAntragsMetadata, not AntragMetaData');
    }
    return this._antragsMetadata as LegacyAntragsMetadata[];
  }

  public moveBelegToTop(beleg: Beleg): void {
    const belegIndex = this._belege().indexOf(beleg);
    const topIndex = this.firstBelegMandatoryConfig.isActive ? 1 : 0;

    // only move up if it's not already at the top
    if (belegIndex < topIndex + 1) {
      return;
    }

    const updatedBelege = [...this._belege()];
    updatedBelege.splice(belegIndex, 1);
    updatedBelege.splice(topIndex, 0, beleg);

    this._belege.set(updatedBelege);
  }

  public addOrUpdateSingleMetaDatum(antragsMetaDatum: LegacyAntragsMetadata): void {
    const indexOfMetaDatum = this._antragsMetadata.findIndex(
      (x) => 'keyInternal' in x && x.keyInternal === antragsMetaDatum.keyInternal,
    );
    if (indexOfMetaDatum > -1) {
      this._antragsMetadata.splice(indexOfMetaDatum, 1, antragsMetaDatum);
      return;
    }

    this._antragsMetadata.push(antragsMetaDatum as LegacyAntragsMetadata & AntragMetaData);
  }

  public removeBeleg(beleg: Beleg): void {
    const belegIndexToDelete = this._belege().indexOf(beleg);

    if (belegIndexToDelete !== -1) {
      const updatedBelege = this._belege().filter((b) => b !== beleg);

      this._belege.set(updatedBelege);
    }
  }

  public getFlags(): AntragsFlag[] {
    const flags: AntragsFlag[] = [];

    if (this.firstBelegMandatoryConfig.isActive && this.isLangantrag) {
      flags.push('HasDedicatedFirst');
    }

    return flags;
  }

  public isContentEmpty(): boolean {
    return !this.hasAntragsMetadata() && !this.hasSeiten();
  }

  public canBeDiscarded(): boolean {
    return this.isContentEmpty() && this.isPristine && !this.antragsId;
  }

  public clone(): Antrag {
    const cloned = new Antrag(this.firstBelegMandatoryConfig);

    if (this._antragsId) {
      cloned.setDataAfterCreationOrReset(this._antragsId, this._timestamp);
    }

    cloned.setAntragsType(this._antragsType);
    cloned.belege = [...this._belege()];
    cloned.doNotFinishMe = this.doNotFinishMe;
    cloned.hasAcceptedDataProtection = this.hasAcceptedDataProtection;

    if (this._containerId) {
      cloned.containerId = this._containerId;
    }

    cloned.isPristine = this._isPristine;
    cloned.setSurveyData(this._surveyData);

    if (this._isCompleted) {
      cloned.setComplete();
    }

    if (this._antragsMetadata.length > 0) {
      cloned.setAntragsData(this._antragsMetadata.slice());
    }

    return cloned;
  }

  private nextBelegNumber(belegNumber?: number): number {
    const { assignedNumber, nextCounter } = nextMonotonicNumber(belegNumber, this._belegCounter);
    this._belegCounter = nextCounter;
    return assignedNumber;
  }
}

export type AntragsMetadataHelper = Omit<LegacyAntragsMetadata, 'value'> & { valueFromForm: (form: unknown) => string };
